import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Grid,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPrinter, IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { api } from "../api/client";
import type { ActSuggest } from "../api/types";

interface Props {
  opened: boolean;
  onClose: () => void;
  shipmentIds: number[];
}

export function ExportActModal({ opened, onClose, shipmentIds }: Props) {
  const { t } = useTranslation();
  const [suggest, setSuggest] = useState<ActSuggest | null>(null);
  const [actNumber, setActNumber] = useState("");
  const [actDate, setActDate] = useState("");
  const [notes, setNotes] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!opened) return;
    setSuggest(null);
    setPreviewUrl(null);
    if (shipmentIds.length === 0) return;
    api.suggestAct(shipmentIds).then((s) => {
      setSuggest(s);
      setActNumber(s.act_number_suggested);
      setActDate(s.act_date_suggested);
      setNotes(s.default_notes);
    }).catch(() => {
      notifications.show({ color: "red", message: t("exportAct.suggestFailed") });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, shipmentIds.join(",")]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClose() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onClose();
  }

  async function generate() {
    if (!suggest?.same_customer) return;
    if (!actNumber.trim() || !actDate) return;
    setGenerating(true);
    try {
      const blob = await api.generateActPdf({
        shipment_ids: shipmentIds,
        act_number: actNumber.trim(),
        act_date: actDate,
        notes: notes.trim(),
      });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      notifications.show({ color: "red", message: t("exportAct.generateFailed") });
    } finally {
      setGenerating(false);
    }
  }

  function handlePrint() {
    iframeRef.current?.contentWindow?.print();
  }

  const downloadName = `Act_${actNumber.replace(/[\/ ]/g, "-")}_${actDate}.pdf`;
  const canGenerate =
    !!suggest?.same_customer && actNumber.trim().length > 0 && actDate.length > 0 && !generating;

  return (
    <Modal opened={opened} onClose={handleClose} size="90%" title={t("exportAct.title")}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="sm">
            {suggest && !suggest.same_customer && (
              <Alert color="red" title={t("exportAct.multiCustomerTitle")}>
                {t("exportAct.multiCustomerBody")}
              </Alert>
            )}
            {suggest?.same_customer && (
              <Paper withBorder p="sm">
                <Stack gap={4}>
                  <Title order={5}>{t("exportAct.summary")}</Title>
                  <Text size="sm">
                    {t("exportAct.customer")}: <b>{suggest.customer_name}</b>
                  </Text>
                  <Text size="sm">
                    {t("exportAct.shipments")}:{" "}
                    {suggest.shipments_summary.map((s) => s.code).join(", ")}
                  </Text>
                  <Text size="sm">
                    {t("exportAct.totals", {
                      devices: suggest.devices_total,
                      boxes: suggest.boxes_total,
                    })}
                  </Text>
                </Stack>
              </Paper>
            )}
            <TextInput
              label={t("exportAct.actNumber")}
              value={actNumber}
              onChange={(e) => setActNumber(e.currentTarget.value)}
              required
            />
            <TextInput
              label={t("exportAct.actDate")}
              type="date"
              value={actDate}
              onChange={(e) => setActDate(e.currentTarget.value)}
              required
            />
            <Textarea
              label={t("exportAct.notes")}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              minRows={4}
              autosize
            />
            <Button
              leftSection={<IconRefresh size={14} />}
              onClick={generate}
              loading={generating}
              disabled={!canGenerate}
            >
              {t("exportAct.generate")}
            </Button>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          {previewUrl ? (
            <iframe
              ref={iframeRef}
              src={previewUrl}
              style={{ width: "100%", height: "70vh", border: "1px solid #e5e5e5" }}
              title="Act preview"
            />
          ) : (
            <Paper withBorder p="xl">
              <Text c="dimmed" size="sm" ta="center">
                {t("exportAct.previewHint")}
              </Text>
            </Paper>
          )}
        </Grid.Col>
      </Grid>

      <Group justify="flex-end" mt="md">
        <Button
          variant="light"
          leftSection={<IconPrinter size={14} />}
          onClick={handlePrint}
          disabled={!previewUrl}
        >
          {t("exportAct.print")}
        </Button>
        <Anchor
          component="a"
          href={previewUrl ?? "#"}
          download={previewUrl ? downloadName : undefined}
          underline="never"
        >
          <Button variant="light" disabled={!previewUrl}>
            {t("exportAct.download")}
          </Button>
        </Anchor>
        <Button variant="subtle" onClick={handleClose}>
          {t("common.cancel")}
        </Button>
      </Group>
    </Modal>
  );
}
